import type { InvoiceDraft, InvoiceLine } from '../../types';
import { formatInvoiceNumber, parseInvoiceSequence } from '../../types';
import { DEFAULT_LABELS } from './standard';

const INVOICE_SINGLE_PAGE_MAX_ITEMS = 6;
const INVOICE_FIRST_PAGE_OVERFLOW_ITEMS = 7;
const INVOICE_CONTINUATION_PAGE_ITEMS = 12;
const INVOICE_LAST_PAGE_FOOTER_ITEMS = 5;

/** Split line items across print/PDF pages (A4). */
export function paginateInvoiceItems<T = InvoiceLine>(items: T[]): T[][] {
  const list = items ?? [];
  if (list.length === 0) return [[]];
  if (list.length <= INVOICE_SINGLE_PAGE_MAX_ITEMS) return [list];

  const rest = list.slice();
  const pages: T[][] = [
    rest.splice(0, Math.min(INVOICE_FIRST_PAGE_OVERFLOW_ITEMS, rest.length)),
  ];

  while (rest.length > INVOICE_LAST_PAGE_FOOTER_ITEMS) {
    const take = Math.min(
      INVOICE_CONTINUATION_PAGE_ITEMS,
      rest.length - INVOICE_LAST_PAGE_FOOTER_ITEMS,
    );
    pages.push(rest.splice(0, take));
  }

  if (rest.length > 0) pages.push(rest);
  return pages;
}

export function createInvoiceId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `inv-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function hydrateInvoice(invoice: InvoiceDraft): InvoiceDraft {
  return {
    ...invoice,
    fromCompany: invoice.fromCompany ?? '',
    slipCompanyName: invoice.slipCompanyName ?? '',
    slipCompanyAddress: invoice.slipCompanyAddress ?? '',
    labels: { ...DEFAULT_LABELS, ...(invoice.labels ?? {}) },
    theme: invoice.theme ?? 'classic',
    shipTo: invoice.shipTo ?? '',
    poNumber: invoice.poNumber ?? '',
    paymentTerms: invoice.paymentTerms ?? '',
    notes: invoice.notes ?? '',
    terms: invoice.terms ?? '',
    amountPaid: invoice.amountPaid ?? 0,
    taxMode: invoice.taxMode ?? 'percent',
    taxValue: invoice.taxValue ?? 0,
    discountMode: invoice.discountMode ?? 'off',
    discountValue: invoice.discountValue ?? 0,
    shippingMode: invoice.shippingMode ?? 'off',
    shippingValue: invoice.shippingValue ?? 0,
    showDiscount: invoice.showDiscount ?? false,
    showShipping: invoice.showShipping ?? false,
    showTax: invoice.showTax ?? false,
    createdAt: invoice.createdAt ?? invoice.updatedAt ?? '',
    customerId: invoice.customerId ?? '',
    currency: invoice.currency ?? 'USD',
    titleEn: invoice.titleEn || invoice.labels?.title || 'INVOICE',
  };
}

export function nextInvoiceNumberFromList(invoices: InvoiceDraft[]): string {
  let maxNum = 0;
  const count = invoices.length;
  for (const inv of invoices) {
    const seq = parseInvoiceSequence(inv.number);
    if (seq > 0 && seq <= count + 100) {
      maxNum = Math.max(maxNum, seq);
    }
  }
  return formatInvoiceNumber(Math.max(maxNum, count) + 1);
}

export function duplicateInvoice(
  source: InvoiceDraft,
  nextNumber = '',
): InvoiceDraft {
  const now = new Date().toISOString();
  return {
    ...source,
    id: createInvoiceId(),
    number: nextNumber,
    date: '',
    createdAt: now,
    updatedAt: now,
    items: source.items.map((item) => ({ ...item })),
  };
}

export function invoicePdfFilename(invoice: InvoiceDraft): string {
  const n = (invoice.number || 'draft').replace(/[^\w.-]+/g, '_');
  return `Invoice-${n}.pdf`;
}
